import React, { useEffect } from 'react';
import { Actions, Alert, Box, Button, Drawer, IconDate, IconEducation, IconLocation, IconMail, IconMoney, IconProfile, IconTime, Inline, Stack, Table, TableHeader, TableBody, TableCell, TableFooter, TableHeaderCell, TableRow, Text, TextField } from 'braid-design-system';
import { useFinancialDate } from '../contexts/FinancialDateProvider';
import { useAuth } from '../contexts/AuthProvider';
import { useCashier } from '../contexts/CashierProvider';
import { acceptPendingCashIn, cashierCashOut, cashierSignOff, getCashierSignOutStatus, getPendingCashIn, getSignedInCashiers } from '../services/cashierService';
import Decimal from 'decimal.js';
import { useToast } from '../hooks/useToast';
import { useLogger } from '../hooks/useLogger';
import Loading from '../components/Loading';
import { useLocalDateTime } from '../contexts/LocalDateTimeProvider';
import SendChequesToBankDialog from '../components/SendChequesToBank/SendChequesToBankDialog';
import { Schemas } from '../types/api-types';
import { useTranslation } from 'react-i18next';

type CashierSignOffProps = {
    setCashierDrawerOpen?: (open: boolean) => void;
};

type CashierSignOffRequest = Schemas['CashierSignOffRequest']
type CashierSignOffDataResponse = Schemas['CashierSignOffDataResponse']

type CashierPendingCashInResponse = {
    total_cash_in: number;
    items: Array<{ id: number; cash_in: number; detail?: string | null }>;
};

type SignedInCashier = {
    it_cashier_session_id: number;
    it_cashier_session_cash_book_id: number;
    it_user_master_id: number;
    user_name: string;
};

const CashierSignOff: React.FC<CashierSignOffProps> = ({ setCashierDrawerOpen }) => {
    const { currentFinancialDate } = useFinancialDate();
    const { user } = useAuth();
    const { cashBook, setCashBook, setIsSignedIn } = useCashier()
    const [cashierSignOutStatus, setCashierSignOutStatus] = React.useState<CashierSignOffDataResponse | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState('');
    const [isChequeDialogOpen, setIsChequeDialogOpen] = React.useState(false);
    const [pendingCashIn, setPendingCashIn] = React.useState<CashierPendingCashInResponse | null>(null);
    const [acceptingCashIn, setAcceptingCashIn] = React.useState(false);
    const [submittingCashOut, setSubmittingCashOut] = React.useState(false);
    const [cashOutDrawerOpen, setCashOutDrawerOpen] = React.useState(false);
    const [signedInCashiers, setSignedInCashiers] = React.useState<SignedInCashier[]>([]);
    const [cashOutAmounts, setCashOutAmounts] = React.useState<Record<number, string>>({});
    const [cashOutSubmitting, setCashOutSubmitting] = React.useState<Record<number, boolean>>({});
    const toast = useToast();
    const logger = useLogger();
    const { dateTime } = useLocalDateTime();
    const { t } = useTranslation();

    const loadSignOutStatus = async () => {
        try {
            const status = await getCashierSignOutStatus();
            setCashierSignOutStatus(status);
        } catch (error: any) {
            setError(
                error.message ||
                t('page.cashier_sign_off.error.load_status', {
                    defaultValue: 'Failed to fetch cashier sign out status',
                })
            );
        }
    };

    const loadPendingCashIn = async () => {
        try {
            const pending = await getPendingCashIn();
            setPendingCashIn(pending);
        } catch (error: any) {
            setPendingCashIn(null);
        }
    };

    const loadSignedInCashiers = async () => {
        try {
            const result = await getSignedInCashiers();
            setSignedInCashiers(result);
        } catch (error: any) {
            toast.showError(
                error.message ||
                t('page.cashier_sign_off.error.load_cashiers', {
                    defaultValue: 'Failed to load signed-in cashiers',
                })
            );
        }
    };

    useEffect(() => {
        loadSignOutStatus();
        loadPendingCashIn();
    }, [t]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (cashierSignOutStatus) {
                const cashierSignOffRequest: CashierSignOffRequest = {
                    ...cashierSignOutStatus,
                    cheque_amount: new Decimal(cashierSignOutStatus.cheque_receipt ?? 0)
                        .minus(cashierSignOutStatus.cheque_payment ?? 0)
                        .toNumber()
                }
                await cashierSignOff(cashierSignOffRequest!);
                setIsSignedIn(false);
                setCashBook(null);
                if (setCashierDrawerOpen) {
                    setCashierDrawerOpen(false);
                }
                toast.showSuccess(
                    t('page.cashier_sign_off.toast.success', {
                        defaultValue: 'Cashier signed off successfully',
                    }),
                );
            }
        } catch (err: any) {
            setError(
                err.message ||
                t('page.cashier_sign_off.error.sign_off_failed', {
                    defaultValue: 'Sign off failed',
                }),
            );
            logger.error('Cashier sign off failed', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptPendingCashIn = async () => {
        setAcceptingCashIn(true);
        try {
            await acceptPendingCashIn();
            toast.showSuccess(
                t('page.cashier_sign_off.pending.accept_success', {
                    defaultValue: 'Pending cash-in accepted',
                })
            );
            await loadPendingCashIn();
            await loadSignOutStatus();
        } catch (error: any) {
            toast.showError(
                error.message ||
                t('page.cashier_sign_off.pending.accept_error', {
                    defaultValue: 'Failed to accept pending cash-in',
                })
            );
        } finally {
            setAcceptingCashIn(false);
        }
    };

    const handleCashOutConfirm = async (cashier: SignedInCashier) => {
        const rawValue = cashOutAmounts[cashier.it_cashier_session_id] || '';
        const amount = Number(rawValue);
        if (!Number.isFinite(amount) || amount <= 0) {
            toast.showWarning(
                t('page.cashier_sign_off.cash_out.invalid_amount', {
                    defaultValue: 'Please enter an amount greater than 0.',
                })
            );
            return;
        }
        
        // Prevent cashing out more than the Net Amount
        if (cashierSignOutStatus) {
            const cashReceipt = Number(cashierSignOutStatus.cash_receipt ?? 0);
            const cashPayment = Number(cashierSignOutStatus.cash_payment ?? 0);
            const closingBase = Number(cashierSignOutStatus.closing_cash ?? 0);
            const displayedClosing = new Decimal(closingBase).plus(cashReceipt).minus(cashPayment).toNumber();
            const chequeReceipt = Number(cashierSignOutStatus.cheque_receipt ?? 0);
            const chequePayment = Number(cashierSignOutStatus.cheque_payment ?? 0);
            const netAmount = new Decimal(displayedClosing).plus(chequeReceipt).minus(chequePayment).toNumber();

            if (amount > netAmount) {
                toast.showWarning(
                    t('page.cashier_sign_off.cash_out.exceeds_net', {
                        defaultValue: 'Amount exceeds available Net Amount.',
                    })
                );
                return;
            }
        }

        setCashOutSubmitting((prev) => ({ ...prev, [cashier.it_cashier_session_id]: true }));
        setSubmittingCashOut(true);
        try {
            await cashierCashOut({
                receiver_cashier_session_id: cashier.it_cashier_session_id,
                amount,
            });
            toast.showSuccess(
                t('page.cashier_sign_off.cash_out.success', {
                    defaultValue: 'Cash out processed successfully',
                })
            );
            setCashOutAmounts((prev) => ({ ...prev, [cashier.it_cashier_session_id]: '' }));
            await loadPendingCashIn();
            await loadSignOutStatus();
        } catch (error: any) {
            toast.showError(
                error.message ||
                t('page.cashier_sign_off.cash_out.error', {
                    defaultValue: 'Failed to process cash out',
                })
            );
        } finally {
            setCashOutSubmitting((prev) => ({ ...prev, [cashier.it_cashier_session_id]: false }));
            setSubmittingCashOut(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }} autoComplete="off">
            <Stack space="small">

                {(!cashierSignOutStatus) && (
                    <Loading
                        message={t('page.cashier_sign_off.loading.fetching', {
                            defaultValue: 'Fetching transaction details. Please wait…',
                        })}
                    />
                )}
                {loading && (
                    <Loading
                        message={t('page.cashier_sign_off.loading.signing_off', {
                            defaultValue: 'Signing you off. Please wait…',
                        })}
                    />
                )}
                {acceptingCashIn && (
                    <Loading
                        message={t('page.cashier_sign_off.loading.accepting', {
                            defaultValue: 'Accepting cash-in. Please wait…',
                        })}
                    />
                )}
                {submittingCashOut && (
                    <Loading
                        message={t('page.cashier_sign_off.loading.cash_out', {
                            defaultValue: 'Processing cash out. Please wait…',
                        })}
                    />
                )}
                <Stack space="large">
                    {!!error &&
                        <Box width="full">
                            <Alert tone="critical"><Text>{error}</Text></Alert>
                        </Box>
                    }
                    <Stack space="small">
                        <Text icon={<IconProfile />}>
                            {user
                                ? user.name
                                : t('page.common.loading_generic', {
                                    defaultValue: 'loading...',
                                })}
                        </Text>
                        <Text icon={<IconLocation />} tone="secondary">
                            {user
                                ? user.active_branch?.name_ln1
                                : t('page.common.loading_generic', {
                                    defaultValue: 'loading...',
                                })}
                        </Text>
                        <Text icon={<IconDate />} tone="secondary" >
                            {currentFinancialDate
                                ? new Date(currentFinancialDate.business_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                })
                                : t('page.common.loading_generic', {
                                    defaultValue: 'loading...',
                                })}
                        </Text>
                        <Text icon={<IconTime />} tone="secondary">
                            {dateTime.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true,
                            })}
                        </Text>
                        <Text icon={<IconEducation />} tone="secondary">
                            {cashBook
                                ? cashBook.cash_book_name
                                : t('page.common.loading_generic', {
                                    defaultValue: 'loading...',
                                })}
                        </Text>
                    </Stack>
                    {pendingCashIn && pendingCashIn.total_cash_in > 0 ? (
                        <Box
                            padding="medium"
                            background="surface"
                            borderRadius="standard"
                            style={{ border: '1px solid #eee' }}
                        >
                            <Stack space="small">
                                <Text weight="strong">
                                    {t('page.cashier_sign_off.pending.title', {
                                        defaultValue: 'Pending cash-in',
                                    })}
                                </Text>
                                <Text tone="secondary" size="small">
                                    {t('page.cashier_sign_off.pending.total', {
                                        defaultValue: 'Total pending: {{amount}}',
                                        amount: new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(Number(pendingCashIn.total_cash_in || 0))
                                    })}
                                </Text>
                                <Box display="flex" disabled={acceptingCashIn}>
                                    <Inline space="small" align="left">
                                        <Button
                                            tone="brandAccent"
                                            onClick={handleAcceptPendingCashIn}
                                        >
                                            {acceptingCashIn
                                                ? t('page.cashier_sign_off.pending.accepting', { defaultValue: 'Accepting...' })
                                                : t('page.cashier_sign_off.pending.accept', { defaultValue: 'Accept' })}
                                        </Button>
                                    </Inline>
                                </Box>
                            </Stack>
                        </Box>
                    ) : null}
                    <Stack space="small">
                        <Table
                            label={t('page.cashier_sign_off.table.label', {
                                defaultValue: 'Cashier summary',
                            })}
                        >
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMoney />} tone="positive" weight='strong'>
                                            {t('page.cashier_sign_off.table.opening_cash', {
                                                defaultValue: 'Opening Cash:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right'>
                                        <Text tone="positive" weight='strong'>
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.opening_cash ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMoney />} tone="positive">
                                            {t('page.cashier_sign_off.table.cash_in', {
                                                defaultValue: 'Cash In:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="positive">
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.cash_in ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMoney />} tone="positive">
                                            {t('page.cashier_sign_off.table.cash_receipt', {
                                                defaultValue: 'Cash Receipt:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="positive">
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.cash_receipt ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMail />} tone="caution">
                                            {t('page.cashier_sign_off.table.cheque_receipt', {
                                                defaultValue: 'Cheque Receipt:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="caution">
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.cheque_receipt ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableHeaderCell align='right'>
                                        <Text weight='strong'>
                                            {t('page.cashier_sign_off.table.subtotal_in', {
                                                defaultValue: 'Sub Total:',
                                            })}
                                        </Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align='right' >
                                        <Text weight='strong'>
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(
                                                    Number(
                                                        new Decimal(
                                                            cashierSignOutStatus.opening_cash || 0,
                                                        )
                                                            .plus(cashierSignOutStatus.cash_in || 0)
                                                            .plus(cashierSignOutStatus.cash_receipt || 0)
                                                            .toFixed(2),
                                                    ),
                                                )
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableHeaderCell>
                                </TableRow>


                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMoney />} tone="critical">
                                            {t('page.cashier_sign_off.table.cash_out', {
                                                defaultValue: 'Cash Out:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="critical">
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.cash_out ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMoney />} tone="critical">
                                            {t('page.cashier_sign_off.table.cash_payments', {
                                                defaultValue: 'Cash Payments:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="critical">
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.cash_payment ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMail />} tone="caution">
                                            {t('page.cashier_sign_off.table.cheque_payment', {
                                                defaultValue: 'Cheque Payment:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="caution">
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(Number(cashierSignOutStatus.cheque_payment ?? 0))
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableHeaderCell align='right'>
                                        <Text weight='strong'>
                                            {t('page.cashier_sign_off.table.subtotal_out', {
                                                defaultValue: 'Sub Total:',
                                            })}
                                        </Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align='right' >
                                        <Text weight='strong'>
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(
                                                    Number(
                                                        new Decimal(
                                                            cashierSignOutStatus.cash_out || 0,
                                                        )
                                                            .plus(cashierSignOutStatus.cash_payment || 0)
                                                            .toFixed(2),
                                                    ),
                                                )
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableHeaderCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell>
                                        <Text icon={<IconMoney />} tone="caution" weight='strong'>
                                            {t('page.cashier_sign_off.table.closing_cash', {
                                                defaultValue: 'Closing Cash:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right'>
                                        <Text tone="caution" weight='strong'>
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(
                                                    Number(
                                                        new Decimal(
                                                            cashierSignOutStatus.opening_cash || 0,
                                                        )
                                                            .plus(cashierSignOutStatus.cash_in || 0)
                                                            .plus(cashierSignOutStatus.cash_receipt || 0)
                                                            .minus(Number(cashierSignOutStatus.cash_out ?? 0))
                                                            .minus(Number(cashierSignOutStatus.cash_payment ?? 0))
                                                            .toFixed(2),
                                                    ),
                                                )
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Text tone="caution" icon={<IconMail />} weight='strong'>
                                            {t('page.cashier_sign_off.table.cheque_amount', {
                                                defaultValue: 'Cheque Amount:',
                                            })}
                                        </Text>
                                    </TableCell>
                                    <TableCell align='right' >
                                        <Text tone="caution" weight='strong'>
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(
                                                    Number(
                                                        new Decimal(Number(cashierSignOutStatus.cheque_receipt || 0))
                                                            .minus(Number(cashierSignOutStatus.cheque_payment || 0))
                                                            .toFixed(2),
                                                    ),
                                                )
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableHeaderCell align='right'>
                                        <Text tone="info" weight='strong'>
                                            {t('page.cashier_sign_off.table.net_amount', {
                                                defaultValue: 'Net Amount:',
                                            })}
                                        </Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align='right' >
                                        <Text tone="info" weight='strong'>
                                            {cashierSignOutStatus
                                                ? new Intl.NumberFormat('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(
                                                    Number(
                                                        new Decimal(
                                                            cashierSignOutStatus.opening_cash || 0,
                                                        )
                                                            .plus(cashierSignOutStatus.cash_in || 0)
                                                            .plus(cashierSignOutStatus.cash_receipt || 0)
                                                            .minus(Number(cashierSignOutStatus.cash_out ?? 0))
                                                            .minus(Number(cashierSignOutStatus.cash_payment ?? 0))
                                                            .plus(Number(cashierSignOutStatus.cheque_receipt ?? 0))
                                                            .minus(Number(cashierSignOutStatus.cheque_payment ?? 0))
                                                            .toFixed(2),
                                                    ),
                                                )
                                                : t('page.common.loading_generic', {
                                                    defaultValue: 'loading...',
                                                })}
                                        </Text>
                                    </TableHeaderCell>
                                </TableRow>
                                
                            </TableFooter>
                        </Table>
                    </Stack>
                    <Box display="flex" justifyContent="flexEnd">
                        <Actions>
                            <Button>
                                {t('page.cashier_sign_off.buttons.print', {
                                    defaultValue: 'Print',
                                })}
                            </Button>
                            <Button
                                onClick={() => {
                                    setCashOutDrawerOpen(true);
                                    loadSignedInCashiers();
                                }}
                            >
                                {t('page.cashier_sign_off.buttons.cash_out', {
                                    defaultValue: 'Cash Out',
                                })}
                            </Button>
                            <Button onClick={() => setIsChequeDialogOpen(true)}>
                                {t('page.cashier_sign_off.buttons.send_cheques', {
                                    defaultValue: 'Send Cheques to Bank',
                                })}
                            </Button>
                            {(loading || !cashierSignOutStatus) ? (
                                <Button variant="transparent" loading>
                                    {t('page.cashier_sign_off.buttons.sign_off', {
                                        defaultValue: 'Sign Off',
                                    })}
                                </Button>
                            ) : (
                                <Button type="submit">
                                    {t('page.cashier_sign_off.buttons.sign_off', {
                                        defaultValue: 'Sign Off',
                                    })}
                                </Button>
                            )}
                        </Actions>
                    </Box>
                </Stack>

            </Stack>
            <SendChequesToBankDialog
                isOpen={isChequeDialogOpen}
                onClose={() => setIsChequeDialogOpen(false)}
            />
            <Drawer
                title={t('page.cashier_sign_off.cash_out.drawer_title', { defaultValue: 'Cash Out' })}
                open={cashOutDrawerOpen}
                onClose={() => setCashOutDrawerOpen(false)}
                width="medium"
            >
                <Stack space="small">
                    <Table label={t('page.cashier_sign_off.cash_out.table_label', { defaultValue: 'Signed-in cashiers' })}>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>
                                    <Text weight="strong" size="small">{t('page.cashier_sign_off.cash_out.session_id', { defaultValue: 'Session ID' })}</Text>
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    <Text weight="strong" size="small">{t('page.cashier_sign_off.cash_out.user', { defaultValue: 'User' })}</Text>
                                </TableHeaderCell>
                                <TableHeaderCell align="right">
                                    <Text weight="strong" size="small">{t('page.cashier_sign_off.cash_out.amount', { defaultValue: 'Amount' })}</Text>
                                </TableHeaderCell>
                                <TableHeaderCell align="center">
                                    <Text weight="strong" size="small">{t('page.cashier_sign_off.cash_out.action', { defaultValue: 'Action' })}</Text>
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {signedInCashiers
                                .filter((cashier) => cashierSignOutStatus?.it_cashier_session_id !== cashier.it_cashier_session_id)
                                .map((cashier) => (
                                    <TableRow key={cashier.it_cashier_session_id}>
                                        <TableCell>
                                            <Text size="small">{cashier.it_cashier_session_id}</Text>
                                        </TableCell>
                                        <TableCell>
                                            <Stack space="xsmall">
                                                <Text size="small">{cashier.user_name}</Text>
                                                <Text size="xsmall" tone="secondary">{cashier.it_user_master_id}</Text>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box style={{ minWidth: '160px' }}>
                                                <TextField
                                                    aria-label={t('page.cashier_sign_off.cash_out.amount', { defaultValue: 'Amount' })}
                                                    value={cashOutAmounts[cashier.it_cashier_session_id] || ''}
                                                    onChange={(value) => {
                                                        const nextValue =
                                                            typeof value === 'string'
                                                                ? value
                                                                : value.currentTarget?.value ?? '';
                                                        setCashOutAmounts((prev) => ({
                                                            ...prev,
                                                            [cashier.it_cashier_session_id]: nextValue,
                                                        }));
                                                    }}
                                                    placeholder={t('page.cashier_sign_off.cash_out.amount_placeholder', { defaultValue: 'Enter amount' })}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Inline alignY="center" space="small">
                                                <Box disabled={cashOutSubmitting[cashier.it_cashier_session_id]}>
                                                    <Button
                                                        tone="brandAccent"
                                                        onClick={() => handleCashOutConfirm(cashier)}
                                                    >
                                                        {cashOutSubmitting[cashier.it_cashier_session_id]
                                                            ? t('page.cashier_sign_off.cash_out.processing', { defaultValue: 'Processing...' })
                                                            : t('page.cashier_sign_off.cash_out.confirm', { defaultValue: 'Confirm' })}
                                                    </Button>
                                                </Box>
                                            </Inline>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {signedInCashiers.filter((cashier) => cashierSignOutStatus?.it_cashier_session_id !== cashier.it_cashier_session_id).length === 0 ? (
                                <TableRow>
                                    <TableCell colspan={4} align="center">
                                        <Text tone="secondary" size="small">
                                            {t('page.cashier_sign_off.cash_out.empty', { defaultValue: 'No other signed-in cashiers found.' })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </Stack>
            </Drawer>
        </form>
    );
};

export default CashierSignOff;


